import {
  createContext,
  useContext,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import type {
  DesignerDocument,
  EditCommand,
  TokenRegistry,
} from '@bq/designer-core';
import { CommandStack } from '@bq/designer-core';

interface DesignerContextValue {
  document: DesignerDocument;
  registry: TokenRegistry;
  execute: (cmd: EditCommand) => void;
  undo: () => void;
  redo: () => void;
  reset: (doc?: DesignerDocument) => void;
  canUndo: boolean;
  canRedo: boolean;
}

const DesignerContext = createContext<DesignerContextValue | null>(null);

export interface DesignerProviderProps {
  commandStack: CommandStack;
  registry: TokenRegistry;
  children: ReactNode;
}

export function DesignerProvider({
  commandStack,
  registry,
  children,
}: DesignerProviderProps) {
  const document = useSyncExternalStore(
    commandStack.subscribe,
    commandStack.getSnapshot,
  );

  const value: DesignerContextValue = {
    document,
    registry,
    execute: (cmd) => commandStack.execute(cmd),
    undo: () => commandStack.undo(),
    redo: () => commandStack.redo(),
    reset: (doc?) => commandStack.reset(doc),
    canUndo: commandStack.canUndo,
    canRedo: commandStack.canRedo,
  };

  return (
    <DesignerContext.Provider value={value}>
      {children}
    </DesignerContext.Provider>
  );
}

export function useDesigner(): DesignerContextValue {
  const ctx = useContext(DesignerContext);
  if (!ctx) {
    throw new Error('useDesigner must be used within a <DesignerProvider>');
  }
  return ctx;
}
